import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import { Progress } from "@/components/ui/progress"
import { Button } from "@/components/ui/button"
import { CheckCircle2, Circle, ChevronUp, ChevronDown } from "lucide-react"
import { useCurrentOrg, orgPath } from "@/hooks/use-current-org"
import { getOnboardingProgress } from "@/services/onboarding"
import { journeyProgress } from "@/lib/onboarding-journey"
import type { OnboardingProgress } from "@/types/onboarding"
import { cn } from "@/lib/utils"

export function SidebarOnboardingWidget() {
  const orgSlug = useCurrentOrg()
  const [isExpanded, setIsExpanded] = useState(false)

  const { data } = useQuery<OnboardingProgress>({
    queryKey: ["onboarding", "progress"],
    queryFn: getOnboardingProgress,
    staleTime: 1000 * 60 * 2,
  })

  if (!data) return null

  // Même avancement que « Votre parcours » : « Compte créé » plus le parcours de base.
  const { percentage } = journeyProgress(data)
  const completedSet = new Set(data.journey.filter((step) => step.completed).map((step) => step.mission_key))
  const nextSuggestion = data.journey.find((step) => !step.completed)

  if (data.base_completed) return null

  return (
    <div className="border-t border-border/60 px-3 py-2" data-test="sidebar-onboarding-widget">
      {/* Collapsed view: icon + percentage */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors hover:bg-accent/50"
        aria-expanded={isExpanded}
        aria-controls="sidebar-onboarding-content"
      >
        <div className="relative flex h-6 w-6 items-center justify-center">
          <svg viewBox="0 0 24 24" className="h-6 w-6 -rotate-90">
            <circle
              cx="12" cy="12" r="10"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="text-muted/40"
            />
            <circle
              cx="12" cy="12" r="10"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray={`${percentage * 0.628} 62.8`}
              className="text-primary"
            />
          </svg>
        </div>
        {/* Avancement des missions de prise en main, pas un taux de
            conformité : le libellé le dit. */}
        <span className="flex-1 text-left font-medium" data-test="sidebar-onboarding-progress">
          Prise en main : {percentage} %
        </span>
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/60" />
        ) : (
          <ChevronUp className="h-3.5 w-3.5 text-muted-foreground/60" />
        )}
      </button>

      {/* Expanded view */}
      {isExpanded && (
        <div id="sidebar-onboarding-content" className="mt-2 space-y-2 px-1">
          <Progress
            value={percentage}
            aria-valuenow={percentage}
            aria-valuemin={0}
            aria-valuemax={100}
            className="h-1.5"
          />
          <ul className="space-y-0.5" role="list">
            {data.journey.slice(0, 5).map((suggestion) => {
              const completed = completedSet.has(suggestion.mission_key)
              return (
                <li key={suggestion.mission_key}>
                  <Link
                    to={orgPath(orgSlug, suggestion.route)}
                    className={cn(
                      "flex items-center gap-2 rounded px-2 py-1 text-xs transition-colors hover:bg-accent/50",
                      completed && "text-muted-foreground line-through"
                    )}
                  >
                    {completed ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-primary flex-shrink-0" />
                    ) : (
                      <Circle className="h-3.5 w-3.5 text-muted-foreground/40 flex-shrink-0" />
                    )}
                    <span className="truncate">{suggestion.title}</span>
                  </Link>
                </li>
              )
            })}
          </ul>

          {nextSuggestion && (
            <Button asChild size="sm" className="w-full text-xs h-7" data-test="sidebar-next-cta">
              <Link to={orgPath(orgSlug, nextSuggestion.route)}>
                Prochaine étape
              </Link>
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
